import React from "react";
import {useTheme} from "connectui/theme/ThemeProvider";
import {generateStyles} from "connectui/theme/styleUtilities";
import ButtonBase from "./ButtonBase";
import PropTypes from "prop-types";
import {gradientRgb} from "utilities/helperFunctions";

/**
 * BookmarkButtonBase is a variant of the ButtonBase component that renders a button styled as a bookmark tab.
 * It allows customization through the `sx` prop.
 *
 * @param {Object} props - The props for the BookmarkButtonBase component.
 * @param {React.ReactNode} props.children - The content of the button, which can be any valid React node.
 * @param {Boolean} [props.active=true] - Used to indicate if the button is in active/selected mode.
 * @param {Function} [props.onClick] - The click handler for the button.
 * @param {Object} [props.sx={}] - The style object that can be used to override default button styles.
 * @param {Object} props.rest - Any other props that should be passed to the ButtonBase component.
 *
 * @returns {JSX.Element} The rendered BookmarkButtonBase component.
 */
const BookmarkButtonBase = ({children, active, onClick, sx = {}, ...props}) => {
  const {theme} = useTheme();
  const defaultStyles = {
    padding: 0,
    backgroundColor: active
      ? theme.colors.gray200
      : gradientRgb(theme.colors.gray50, 0.7),
    borderRadius: "8px 8px 0px 0px",
    backdropFilter: "blur(3px)",
    boxShadow: "none",
    "&:hover": {backgroundColor: theme.colors.hoverGray},
    "&:disabled": {
      opacity: 1,
      cursor: "not-allowed",
      backgroundColor: gradientRgb(theme.colors.gray300, 0.3),
    },
  };
  const styles = generateStyles(
    {sx: {...defaultStyles, ...sx}, ...props},
    theme,
  );

  return (
    <ButtonBase onClick={onClick} sx={styles} {...props}>
      {children}
    </ButtonBase>
  );
};

BookmarkButtonBase.propTypes = {
  children: PropTypes.node.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  sx: PropTypes.any,
};
export default BookmarkButtonBase;
